import { useState, type FormEvent } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    FormControlLabel,
    Switch,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Delete as DeleteIcon, Send as SendIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import {
    DASHBOARD_AUTH_PATHS,
    DASHBOARD_LINKS,
    type AssignableOrgRole,
    type OrgRole,
} from '@yourownmetric/shared'

import {
    useChangeRoleMutation,
    useCreateInviteMutation,
    useGetOrgQuery,
    useLeaveOrgMutation,
    useListInvitesQuery,
    useListMembersQuery,
    useRemoveMemberMutation,
    useResendInviteMutation,
    useRevokeInviteMutation,
    useSetRequireMfaMutation,
    useTransferOwnershipMutation,
} from '@/apis/teamApi'
import {
    canEditMember,
    grantableRoles,
    ROLE_HELP,
    ROLE_LABELS,
    seatSummary,
    type IssuedInvite,
    type TeamMember,
} from '@/apis/teamTypes'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { logout } from '@/modules/auth/authSlice'

const apiMessage = (err: unknown, fallback: string) =>
    (err as { data?: { error?: { message?: string } } })?.data?.error?.message ?? fallback

const dateOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—')

type Pending =
    | { kind: 'remove'; member: TeamMember }
    | { kind: 'transfer'; member: TeamMember }
    | { kind: 'leave' }
    | null

export function TeamPage() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { data: org } = useGetOrgQuery()
    const { data: members = [], isLoading } = useListMembersQuery()
    const canManage = !!org?.can_manage
    const { data: invites = [] } = useListInvitesQuery(undefined, { skip: !canManage })
    const [createInvite, { isLoading: inviting }] = useCreateInviteMutation()
    const [resendInvite] = useResendInviteMutation()
    const [revokeInvite] = useRevokeInviteMutation()
    const [changeRole] = useChangeRoleMutation()
    const [removeMember] = useRemoveMemberMutation()
    const [transferOwnership] = useTransferOwnershipMutation()
    const [leaveOrg] = useLeaveOrgMutation()
    const [setRequireMfa, { isLoading: savingMfa }] = useSetRequireMfaMutation()

    const myRole: OrgRole = org?.role ?? 'viewer'
    const grantable = grantableRoles(myRole)
    const [email, setEmail] = useState('')
    const [role, setRole] = useState<AssignableOrgRole>('member')
    const [inviteError, setInviteError] = useState<string | null>(null)
    const [issued, setIssued] = useState<IssuedInvite | null>(null)
    const [pending, setPending] = useState<Pending>(null)

    const notify = (message: string, severity: 'success' | 'error' = 'success') =>
        dispatch(showNotification({ message, severity }))

    const seats = org ? seatSummary(org) : null

    const submitInvite = async (e: FormEvent) => {
        e.preventDefault()
        setInviteError(null)
        try {
            const r = await createInvite({ email, role }).unwrap()
            setIssued(r)
            setEmail('')
            notify(
                r.emailed
                    ? `Invitation emailed to ${r.email}`
                    : `Invitation created for ${r.email}`,
            )
        } catch (err) {
            setInviteError(apiMessage(err, 'Could not send the invitation'))
        }
    }

    const run = async (fn: () => Promise<unknown>, ok: string) => {
        try {
            await fn()
            notify(ok)
        } catch (err) {
            notify(apiMessage(err, 'That did not work'), 'error')
        }
    }

    const confirm = async () => {
        const p = pending
        setPending(null)
        if (!p) {
            return
        }
        if (p.kind === 'remove') {
            await run(
                () => removeMember(p.member.user_id).unwrap(),
                `${p.member.email} was removed`,
            )
        } else if (p.kind === 'transfer') {
            await run(
                () => transferOwnership(p.member.user_id).unwrap(),
                `${p.member.email} now owns this organization`,
            )
        } else {
            try {
                await leaveOrg().unwrap()
                dispatch(logout())
                navigate(DASHBOARD_AUTH_PATHS.login)
            } catch (err) {
                notify(apiMessage(err, 'Could not leave'), 'error')
            }
        }
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1000 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Typography variant="h5">Team</Typography>
                {org && (
                    <Chip label={`You: ${ROLE_LABELS[myRole]}`} size="small" variant="outlined" />
                )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                People who can sign in to {org?.name ?? 'this organization'}.{' '}
                {seats && <span data-testid="seat-summary">{seats.label}.</span>}
            </Typography>

            {!canManage && org && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You're a {ROLE_LABELS[myRole].toLowerCase()} here. {ROLE_HELP[myRole]}. Owners
                    and admins manage the team.
                </Alert>
            )}

            {canManage && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Invite someone
                        </Typography>
                        {seats?.full ? (
                            <Alert
                                severity="warning"
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => navigate(DASHBOARD_LINKS.billing)}
                                    >
                                        Upgrade
                                    </Button>
                                }
                            >
                                Your plan includes {org?.member_limit} team member
                                {org?.member_limit === 1 ? '' : 's'}. Pay-as-you-go has unlimited
                                members.
                            </Alert>
                        ) : (
                            <Box
                                component="form"
                                onSubmit={submitInvite}
                                sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                            >
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    size="small"
                                    sx={{ flex: '1 1 260px' }}
                                />
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={role}
                                        label="Role"
                                        onChange={e => setRole(e.target.value as AssignableOrgRole)}
                                    >
                                        {grantable.map(r => (
                                            <MenuItem key={r} value={r}>
                                                {ROLE_LABELS[r]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={inviting}
                                    startIcon={<SendIcon />}
                                >
                                    {inviting ? 'Sending…' : 'Send invite'}
                                </Button>
                            </Box>
                        )}
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {ROLE_HELP[role]}. Invites expire after 7 days and work once, for that
                            email address only.
                        </Typography>
                        {inviteError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {inviteError}
                            </Alert>
                        )}
                        {issued && (
                            <Alert
                                severity={issued.emailed ? 'success' : 'info'}
                                sx={{ mt: 2 }}
                                onClose={() => setIssued(null)}
                            >
                                {issued.emailed
                                    ? `We emailed ${issued.email}. You can also share this link with them:`
                                    : `Share this link with ${issued.email}:`}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                        data-testid="invite-url"
                                    >
                                        {issued.invite_url}
                                    </Typography>
                                    <CopyButton text={issued.invite_url} />
                                </Box>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {canManage && org && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={org.require_mfa}
                                    disabled={savingMfa}
                                    onChange={e => {
                                        const on = e.target.checked
                                        void run(
                                            () => setRequireMfa(on).unwrap(),
                                            on
                                                ? 'Two-step verification is now required for every member'
                                                : 'Two-step verification is optional again',
                                        )
                                    }}
                                    inputProps={{ 'aria-label': 'Require two-step verification' }}
                                />
                            }
                            label="Require two-step verification for every member"
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                        >
                            Members without it are sent to Account → Security until they turn it on.
                            Turn it on for yourself first.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Card sx={{ mb: 3, overflowX: 'auto' }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Members
                    </Typography>
                    <Table size="small" sx={{ minWidth: 640 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>MFA</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell>Last sign-in</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={6}>Loading…</TableCell>
                                </TableRow>
                            )}
                            {members.map(m => {
                                const editable = canEditMember(myRole, m)
                                return (
                                    <TableRow key={m.user_id}>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                component="div"
                                            >
                                                {m.name}{' '}
                                                {m.is_you && (
                                                    <Chip
                                                        label="you"
                                                        size="small"
                                                        sx={{ ml: 0.5 }}
                                                    />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {m.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {editable ? (
                                                <Select
                                                    size="small"
                                                    value={m.role}
                                                    onChange={e =>
                                                        run(
                                                            () =>
                                                                changeRole({
                                                                    userId: m.user_id,
                                                                    role: e.target.value,
                                                                }).unwrap(),
                                                            `${m.email} is now ${ROLE_LABELS[e.target.value as OrgRole].toLowerCase()}`,
                                                        )
                                                    }
                                                    inputProps={{
                                                        'aria-label': `Role of ${m.email}`,
                                                    }}
                                                >
                                                    {Array.from(
                                                        new Set([m.role, ...grantable]),
                                                    ).map(r => (
                                                        <MenuItem
                                                            key={r}
                                                            value={r}
                                                            disabled={
                                                                !grantable.includes(
                                                                    r as AssignableOrgRole,
                                                                )
                                                            }
                                                        >
                                                            {ROLE_LABELS[r]}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <Chip
                                                    label={ROLE_LABELS[m.role]}
                                                    size="small"
                                                    color={
                                                        m.role === 'owner' ? 'primary' : 'default'
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{m.mfa_enabled ? 'On' : 'Off'}</TableCell>
                                        <TableCell>{dateOf(m.joined_at)}</TableCell>
                                        <TableCell>{dateOf(m.last_login_at)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {myRole === 'owner' && !m.is_you && (
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        setPending({ kind: 'transfer', member: m })
                                                    }
                                                >
                                                    Make owner
                                                </Button>
                                            )}
                                            {editable && (
                                                <Tooltip title="Remove from organization">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Remove ${m.email}`}
                                                        onClick={() =>
                                                            setPending({
                                                                kind: 'remove',
                                                                member: m,
                                                            })
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {m.is_you && m.role !== 'owner' && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setPending({ kind: 'leave' })}
                                                >
                                                    Leave
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {canManage && invites.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Pending invitations
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Invited by</TableCell>
                                    <TableCell>Expires</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invites.map(i => (
                                    <TableRow key={i.id}>
                                        <TableCell>{i.email}</TableCell>
                                        <TableCell>{ROLE_LABELS[i.role]}</TableCell>
                                        <TableCell>
                                            {i.invited_by?.name ?? i.invited_by?.email ?? '—'}
                                        </TableCell>
                                        <TableCell>{dateOf(i.expires_at)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        setIssued(await resendInvite(i.id).unwrap())
                                                        notify(`New invitation sent to ${i.email}`)
                                                    } catch (err) {
                                                        notify(
                                                            apiMessage(err, 'Could not resend'),
                                                            'error',
                                                        )
                                                    }
                                                }}
                                            >
                                                Resend
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    run(
                                                        () => revokeInvite(i.id).unwrap(),
                                                        `Invitation to ${i.email} withdrawn`,
                                                    )
                                                }
                                            >
                                                Revoke
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <ConfirmDialog
                open={pending !== null}
                title={
                    pending?.kind === 'remove'
                        ? 'Remove member?'
                        : pending?.kind === 'transfer'
                          ? 'Transfer ownership?'
                          : 'Leave organization?'
                }
                message={
                    pending?.kind === 'remove'
                        ? `${pending.member.email} loses access right away.`
                        : pending?.kind === 'transfer'
                          ? `${pending.member.email} becomes the owner. You stay on as an admin.`
                          : `You lose access to ${org?.name ?? 'this organization'} right away.`
                }
                confirmLabel={
                    pending?.kind === 'transfer'
                        ? 'Transfer'
                        : pending?.kind === 'leave'
                          ? 'Leave'
                          : 'Remove'
                }
                confirmColor={pending?.kind === 'transfer' ? 'warning' : 'error'}
                onConfirm={() => {
                    void confirm()
                }}
                onCancel={() => setPending(null)}
            />
        </Box>
    )
}
